/**
 * @fileoverview Zod schema for the reset-password form.
 * @description The /reset-password form asks for exactly what the set-password form does — a new
 * password checked against the live password policy, and its confirmation — so it builds on the
 * same rules (buildSetPasswordFormSchema) rather than copying them; a policy change can't leave
 * one form checking differently from the other. Every issue message is a translation key,
 * translated only at the display call site in ResetPasswordForm.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { buildSetPasswordFormSchema } from '@/features/auth/schemas/set-password-form.schema'

import type { PasswordPolicy } from '@/features/auth/services/password-policy.service'

/**
 * @description Builds the reset-password schema against the currently fetched password policy.
 * Before the policy has loaded, only the required/match checks apply.
 * @param {PasswordPolicy | undefined} policy - The fetched password policy, once loaded.
 * @returns {ReturnType<typeof buildSetPasswordFormSchema>} The schema to resolve the form against.
 */
export function buildResetPasswordFormSchema(policy: PasswordPolicy | undefined) {
  return buildSetPasswordFormSchema(policy)
}

export type ResetPasswordFormValues = {
  newPassword: string
  confirmPassword: string
}
